/**
 * Nether portal travel: lighting a frame, the 4 s (80 tick) stand-in timer
 * (instant in creative), the 300-tick re-entry cooldown, the 8:1 coordinate
 * scaling, linking to an existing portal (16 blocks into the Nether, 128 back
 * into the overworld) or building a new one with an obsidian platform, the
 * dimension switch itself and the purple portal screen overlay. End portals
 * (game/endPortal.ts) are instant: the overworld one lands on the End's obsidian
 * platform (rebuilt on every arrival), the exit portal sends the player home.
 *
 * main.ts owns one instance and calls tick() each game tick, update() each
 * frame, tryLight() from flint-and-steel and blockChanged() from
 * ChunkManager.onBlockChange.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import { AIR, IS_SOLID, isNetherPortal, LAVA_ID, WATER_AMOUNT } from '../blocks';
import { ChunkState } from '../world/chunk';
import type { ChunkManager } from '../world/manager';
import type { WorldSave } from '../world/save';
import {
  dimensionSettings, type DimensionId, portalDestination, scaleCoord,
} from '../world/dimension';
import { buildSpawnPlatform, END_SPAWN } from '../worldgen/end';
import { inEndPortal } from './endPortal';
import {
  findPortalFrame, findPortalInEdits, portalIdFor, newPortalBlocks, portalCells, portalCellsToBreak,
} from '../world/portal';

export const PORTAL_WAIT_TICKS = 80;
export const PORTAL_COOLDOWN_TICKS = 300;

export interface TravelPlayer {
  readonly position: { x: number; y: number; z: number; set(x: number, y: number, z: number): unknown };
  teleportTo(x: number, y: number, z: number): void;
  setFrozen(f: boolean): void;
}

export interface TravelHost {
  chunks: ChunkManager;
  save: WorldSave;
  player: TravelPlayer;
  creative(): boolean;
  /** Before the switch: persist per-dimension state held outside the save (furnaces). */
  beforeSwitch(from: DimensionId): void;
  /** After the save + chunk manager point at `to`: reload furnaces, clear mobs, sky... */
  afterSwitch(to: DimensionId): void;
  /** Where leaving the End puts the player (bed / world spawn); y is the saved feet y, if any. */
  homeSpawn?(): { x: number; z: number; y?: number };
  toast?(msg: string): void;
}

interface Arrival {
  dim: DimensionId;
  /** Scaled target (search centre). */
  x: number; y: number; z: number;
  /** Resolved feet position once the destination is known. */
  dest: [number, number, number] | null;
  /** End travel: the End's obsidian platform / back to the overworld spawn (no nether portal). */
  kind?: 'end' | 'home';
}

export class DimensionTravel {
  private portalTicks = 0;
  private cooldown = 0;
  private arrival: Arrival | null = null;
  private overlay: HTMLDivElement | null = null;
  private overlayAlpha = 0;
  private breaking = false;
  private homeY: number | undefined;

  constructor(private readonly host: TravelHost) {}

  get dimension(): DimensionId { return this.host.chunks.dimension; }
  /** True while the destination is loading (the player is frozen). */
  get travelling(): boolean { return this.arrival !== null; }
  /** 0..1 progress of standing in a portal (drives the screen overlay). */
  get progress(): number { return Math.min(1, this.portalTicks / PORTAL_WAIT_TICKS); }

  // --- portal blocks ---------------------------------------------------------

  /** Flint and steel on (x, y, z), an empty cell: light a valid frame. */
  tryLight(x: number, y: number, z: number): boolean {
    const w = this.host.chunks.world;
    const frame = findPortalFrame(w, x, y, z);
    if (!frame) return false;
    const chunks = this.host.chunks;
    chunks.batchEdits(() => {
      for (const [px, py, pz] of portalCells(frame)) chunks.setBlock(px, py, pz, portalIdFor(frame.axis));
    });
    return true;
  }

  /** ChunkManager.onBlockChange hook: break portals whose frame lost a block. */
  blockChanged(x: number, y: number, z: number): void {
    if (this.breaking) return;
    const chunks = this.host.chunks;
    const id = chunks.world.getBlock(x, y, z);
    const cells = portalCellsToBreak(chunks.world, x, y, z, id);
    if (cells.length === 0) return;
    // setBlock re-enters onBlockChange for each cell: ignore those
    this.breaking = true;
    try {
      for (const [px, py, pz] of cells) chunks.setBlock(px, py, pz, AIR);
    } finally {
      this.breaking = false;
    }
  }

  // --- per tick --------------------------------------------------------------

  tick(): void {
    if (this.arrival) { this.resolveArrival(); return; }
    const p = this.host.player.position;
    const w = this.host.chunks.world;
    const bx = Math.floor(p.x), by = Math.floor(p.y), bz = Math.floor(p.z);
    const inPortal = isNetherPortal(w.getBlock(bx, by, bz)) || isNetherPortal(w.getBlock(bx, by + 1, bz));
    if (this.cooldown > 0) this.cooldown--;
    if (this.cooldown === 0 && inEndPortal(w, bx, by, bz)) { this.travelEnd(); return; }
    if (!inPortal) {
      // vanilla: the nausea wears off gradually once you step out
      this.portalTicks = Math.max(0, this.portalTicks - 4);
      return;
    }
    if (this.cooldown > 0) return;
    this.portalTicks++;
    if (this.host.creative() || this.portalTicks >= PORTAL_WAIT_TICKS) this.travel();
  }

  /** Switch to the other dimension through the portal the player is standing in. */
  travel(): void {
    const from = this.dimension;
    const to = portalDestination(from);
    const p = this.host.player.position;
    const tx = scaleCoord(p.x, from, to);
    const tz = scaleCoord(p.z, from, to);
    const ty = Math.min(Math.max(p.y, 1), dimensionSettings(to).height - 4);
    this.switchTo(to, tx, ty, tz);
  }

  /** Stepped into an end portal: overworld -> the End's platform, the End -> the overworld spawn. */
  travelEnd(): void {
    if (this.dimension === 'end') {
      const home = this.host.homeSpawn?.() ?? { x: 0.5, z: 0.5 };
      this.switchTo('overworld', home.x, dimensionSettings('overworld').height - 2, home.z, 'home', home.y);
    } else {
      this.switchTo('end', END_SPAWN.x + 0.5, END_SPAWN.y, END_SPAWN.z + 0.5, 'end');
    }
  }

  /**
   * Move the player to `to` near (x, y, z), linking or building a portal there.
   * Exposed for debug commands; normal travel goes through travel().
   */
  switchTo(to: DimensionId, x: number, y: number, z: number, kind?: 'end' | 'home', homeY?: number): void {
    const host = this.host;
    host.beforeSwitch(this.dimension);
    host.save.setDimension(to);
    host.chunks.setDimension(to);
    host.afterSwitch(to);
    this.portalTicks = 0;
    this.cooldown = PORTAL_COOLDOWN_TICKS;
    this.arrival = { dim: to, x, y, z, dest: null, kind };
    this.homeY = homeY;
    host.player.setFrozen(true);
    host.player.position.set(x, Math.min(y, dimensionSettings(to).height - 2), z);
  }

  /**
   * Switch dimension without a portal (respawning in the overworld after dying
   * in the Nether): the caller positions the player itself.
   */
  switchWithoutPortal(to: DimensionId): void {
    if (to === this.dimension) return;
    const host = this.host;
    host.beforeSwitch(this.dimension);
    host.save.setDimension(to);
    host.chunks.setDimension(to);
    host.afterSwitch(to);
    this.portalTicks = 0;
    this.arrival = null;
  }

  /** Destination chunks loaded: pick the exit portal (existing or new) and place the player. */
  private resolveArrival(): void {
    const a = this.arrival!;
    const chunks = this.host.chunks;
    if (a.dest) {
      if (!chunksReady(chunks, a.dest[0], a.dest[2], 0, ChunkState.Ready)) return;
      this.host.player.teleportTo(a.dest[0], a.dest[1], a.dest[2]);
      this.arrival = null;
      return;
    }
    if (a.kind === 'end') {
      // rebuild the 5x5 obsidian platform every arrival (vanilla), then stand on it
      if (!chunksReady(chunks, a.x, a.z, 1, ChunkState.Lit)) return;
      chunks.batchEdits(() => buildSpawnPlatform((bx, by, bz, id) => { chunks.setBlock(bx, by, bz, id); }));
      a.dest = [a.x, END_SPAWN.y, a.z];
      return;
    }
    if (a.kind === 'home') {
      if (!chunksReady(chunks, a.x, a.z, 0, ChunkState.Ready)) return;
      const sy = this.homeY ?? chunks.spawnY(Math.floor(a.x), Math.floor(a.z)) + 1.02;
      if (sy <= 0) return;
      a.dest = [a.x, sy, a.z];
      return;
    }
    const radius = dimensionSettings(a.dim).portalSearchRadius;
    const found = findPortalInEdits(
      (cx, cz) => this.host.save.editsIn(a.dim, cx, cz), Math.floor(a.x), Math.floor(a.z), radius,
    );
    if (found) {
      a.dest = [found[0] + 0.5, found[1], found[2] + 0.5];
      this.host.player.position.set(a.dest[0], a.dest[1], a.dest[2]);
      // drop to the bottom of the portal once its chunk is here (see below)
      if (chunksReady(chunks, found[0], found[2], 0, ChunkState.Ready)) {
        let by = found[1];
        while (by > 0 && isNetherPortal(chunks.world.getBlock(found[0], by - 1, found[2]))) by--;
        a.dest[1] = by;
      }
      return;
    }
    // build a new portal: needs the 5x5 chunks around the target lit
    if (!chunksReady(chunks, a.x, a.z, 2, ChunkState.Lit)) return;
    const spot = findPortalSpot(chunks, Math.floor(a.x), Math.floor(a.y), Math.floor(a.z), a.dim);
    chunks.batchEdits(() => {
      for (const [bx, by, bz, id] of newPortalBlocks(spot[0], spot[1], spot[2])) chunks.setBlock(bx, by, bz, id);
    });
    a.dest = [spot[0] + 1, spot[1], spot[2] + 0.5];
  }

  // --- overlay -----------------------------------------------------------------

  /**
   * Portal screen effect: vanilla draws the animated nether_portal sprite
   * stretched over the screen with alpha rising over the 80-tick timer, plus a
   * sine wobble of the whole image (here a UV distortion in PostFX's composite
   * pass, driven through `wobble`). During a dimension switch the overlay holds
   * nearly opaque until the destination chunks around the player are meshed,
   * then fades out over ~0.5 s, so no half-loaded world shows.
   */
  attachOverlay(parent: HTMLElement, wobble?: { x: number; y: number }): void {
    const d = document.createElement('div');
    d.style.cssText = 'position:fixed;inset:0;pointer-events:none;opacity:0;z-index:5;'
      + 'background-color:rgba(90,10,170,0.35);background-image:url(mc/nether_portal.png);'
      + 'background-size:100% 3200%;background-repeat:no-repeat;image-rendering:pixelated;';
    parent.appendChild(d);
    this.overlay = d;
    this.wobble = wobble ?? null;
  }

  private wobble: { x: number; y: number } | null = null;
  private wobbleStrength = 0;
  private animTime = 0;
  /** Hold the overlay until the world around the player is meshed. */
  private holding = false;
  private holdTime = 0;
  private endHop = false;

  /** Per-frame overlay fade + wobble. */
  update(dt: number): void {
    this.animTime += dt;
    if (this.arrival) {
      this.holding = true;
      this.holdTime = performance.now();
      this.endHop = this.arrival.kind === 'end' || this.arrival.kind === 'home';
    } else if (this.holding) {
      const held = (performance.now() - this.holdTime) / 1000;
      const st = this.host.chunks.stats;
      if (st.wanted > 0 && st.ready >= st.wanted * 0.9) this.holding = false;
      // End portal travel is instant in vanilla: never keep the cover up long
      // (sparse void chunks may never reach 90 % ready)
      else if (held > (this.endHop ? 0.4 : 6)) this.holding = false;
    }
    let target: number;
    let k: number;
    if (this.holding) { target = 0.92; k = Math.min(1, dt * 10); }
    else if (this.portalTicks > 0) { target = 0.15 + this.progress * 0.65; k = Math.min(1, dt * 8); }
    else { target = 0; k = Math.min(1, dt * 6); } // ~0.5 s fade
    this.overlayAlpha += (target - this.overlayAlpha) * k;
    // the wobble ramps with the timer and lingers briefly after leaving
    const wTarget = this.holding ? 1 : this.portalTicks > 0 ? this.progress : 0;
    this.wobbleStrength += (wTarget - this.wobbleStrength) * Math.min(1, dt * (wTarget > this.wobbleStrength ? 4 : 2));
    if (this.wobble) {
      this.wobble.x = this.wobbleStrength < 0.01 ? 0 : this.wobbleStrength;
      this.wobble.y = this.animTime;
    }
    if (!this.overlay) return;
    this.overlay.style.opacity = this.overlayAlpha < 0.01 ? '0' : this.overlayAlpha.toFixed(3);
    if (this.overlayAlpha >= 0.01) {
      // 32-frame strip at vanilla's 2 ticks per frame
      const frame = Math.floor(this.animTime * 10) % 32;
      this.overlay.style.backgroundPosition = `0 ${(frame / 31) * 100}%`;
    }
  }
}

/** Are the chunks within `r` chunks of (x, z) at least in `state`? */
function chunksReady(chunks: ChunkManager, x: number, z: number, r: number, state: ChunkState): boolean {
  const cx = Math.floor(x / CHUNK_SIZE), cz = Math.floor(z / CHUNK_SIZE);
  for (let dz = -r; dz <= r; dz++) {
    for (let dx = -r; dx <= r; dx++) {
      const c = chunks.world.getChunk(cx + dx, cz + dz);
      if (!c || c.state < state) return false;
    }
  }
  return true;
}

/**
 * A spot for a new 2x3 x-axis portal near (x, y, z): interior bottom-left
 * cell. Prefers the nearest place with solid, non-liquid ground under the
 * 4-wide frame and a 4x4 pocket of air; falls back to the target itself
 * (newPortalBlocks lays the obsidian platform and clears the pocket).
 */
export function findPortalSpot(chunks: { world: { getBlock(x: number, y: number, z: number): number } }, x: number, y: number, z: number, dim: DimensionId): [number, number, number] {
  const w = chunks.world;
  const set = dimensionSettings(dim);
  const yMin = dim === 'nether' ? set.seaLevel + 1 : 2;
  const yMax = Math.min(set.height, WORLD_HEIGHT) - 6;
  const ground = (bx: number, by: number, bz: number): boolean => {
    const id = w.getBlock(bx, by, bz);
    return IS_SOLID[id] === 1 && id !== LAVA_ID && WATER_AMOUNT[id] === 0;
  };
  let best: [number, number, number] | null = null;
  let bestD = Infinity;
  for (let dz = -16; dz <= 16; dz++) {
    for (let dx = -16; dx <= 16; dx++) {
      const bx = x + dx, bz = z + dz;
      for (let by = yMin; by <= yMax; by++) {
        if (w.getBlock(bx, by, bz) !== AIR || !ground(bx, by - 1, bz)) continue;
        const d = dx * dx + dz * dz + (by - y) * (by - y) * 0.5;
        if (d >= bestD) continue;
        let ok = true;
        for (let u = -1; u <= 2 && ok; u++) {
          for (let dzz = -1; dzz <= 1 && ok; dzz++) {
            if (!ground(bx + u, by - 1, bz + dzz)) ok = false;
            for (let v = 0; v <= 3 && ok; v++) if (w.getBlock(bx + u, by + v, bz + dzz) !== AIR) ok = false;
          }
        }
        if (ok) { best = [bx, by, bz]; bestD = d; }
      }
    }
  }
  if (best) return best;
  const fy = Math.max(yMin + 1, Math.min(yMax - 4, dim === 'nether' ? 70 : y));
  return [x, fy, z];
}
